/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.platform.notification.NotificationPermissionCheckpoint
import studio.oddpods.cauce.core.base.platform.notification.rememberNotificationPermissionGranted
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.settings.generated.resources.Res
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_advance_15min
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_advance_1d
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_advance_1h
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_advance_2h
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_advance_30min
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_day_friday
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_day_monday
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_day_saturday
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_day_sunday
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_day_thursday
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_day_tuesday
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_day_wednesday
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_disabled_description
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_disabled_title
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_payment_day_label
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_payment_description
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_payment_time_label
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_payment_title
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_payment_toggle
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_permission_description
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_permission_enable
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_permission_not_now
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_permission_title
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_session_advance_label
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_session_description
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_session_title
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_session_toggle
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_notification_title
import studio.oddpods.cauce.feature.settings.notification.NotificationUiState
import studio.oddpods.cauce.feature.settings.notification.NotificationViewModel

private val DAY_LABELS = listOf(
    1 to Res.string.feature_settings_notification_day_monday,
    2 to Res.string.feature_settings_notification_day_tuesday,
    3 to Res.string.feature_settings_notification_day_wednesday,
    4 to Res.string.feature_settings_notification_day_thursday,
    5 to Res.string.feature_settings_notification_day_friday,
    6 to Res.string.feature_settings_notification_day_saturday,
    7 to Res.string.feature_settings_notification_day_sunday,
)

private val ADVANCE_OPTIONS = listOf(
    15 to Res.string.feature_settings_notification_advance_15min,
    30 to Res.string.feature_settings_notification_advance_30min,
    60 to Res.string.feature_settings_notification_advance_1h,
    120 to Res.string.feature_settings_notification_advance_2h,
    1440 to Res.string.feature_settings_notification_advance_1d,
)

@Composable
internal fun NotificationScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: NotificationViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    NotificationScreenContent(
        state = uiState,
        modifier = modifier,
        onBackClick = onBackClick,
        onSessionRemindersToggled = viewModel::onSessionRemindersToggled,
        onPaymentRemindersToggled = viewModel::onPaymentRemindersToggled,
        onSessionAdvanceChanged = viewModel::onSessionAdvanceMinutesChanged,
        onPaymentDayChanged = viewModel::onPaymentDayChanged,
        onPaymentTimeChanged = viewModel::onPaymentTimeChanged,
        onPermissionGranted = viewModel::onPermissionGranted,
    )
}

@Composable
internal fun NotificationScreenContent(
    state: NotificationUiState,
    onBackClick: () -> Unit,
    onSessionRemindersToggled: (Boolean) -> Unit,
    onPaymentRemindersToggled: (Boolean) -> Unit,
    onSessionAdvanceChanged: (Int) -> Unit,
    onPaymentDayChanged: (Int) -> Unit,
    onPaymentTimeChanged: (Int, Int) -> Unit,
    onPermissionGranted: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val isPermissionGranted = rememberNotificationPermissionGranted()
    var requestPermission by remember { mutableStateOf(false) }

    NotificationPermissionCheckpoint(
        shouldRequest = requestPermission,
        title = stringResource(Res.string.feature_settings_notification_permission_title),
        description = stringResource(Res.string.feature_settings_notification_permission_description),
        confirmText = stringResource(Res.string.feature_settings_notification_permission_enable),
        dismissText = stringResource(Res.string.feature_settings_notification_permission_not_now),
        onResult = { granted ->
            requestPermission = false
            if (granted) onPermissionGranted()
        },
        onDismiss = { requestPermission = false },
    ) {
        KptScaffold(
            onNavigationIconClick = onBackClick,
            title = stringResource(Res.string.feature_settings_notification_title),
            modifier = modifier,
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                if (!isPermissionGranted) {
                    PermissionBanner(
                        onEnableClick = { requestPermission = true },
                    )
                }

                PaymentRemindersCard(
                    enabled = state.settings.paymentRemindersEnabled,
                    dayOfWeek = state.settings.paymentReminderDayOfWeek,
                    hour = state.settings.paymentReminderHour,
                    minute = state.settings.paymentReminderMinute,
                    isPermissionGranted = isPermissionGranted,
                    onToggle = { enabled ->
                        if (enabled && !isPermissionGranted) {
                            requestPermission = true
                        } else {
                            onPaymentRemindersToggled(enabled)
                        }
                    },
                    onDayChanged = onPaymentDayChanged,
                    onTimeChanged = onPaymentTimeChanged,
                )

                SessionRemindersCard(
                    enabled = state.settings.sessionRemindersEnabled,
                    advanceMinutes = state.settings.sessionAdvanceMinutes,
                    isPermissionGranted = isPermissionGranted,
                    onToggle = { enabled ->
                        if (enabled && !isPermissionGranted) {
                            requestPermission = true
                        } else {
                            onSessionRemindersToggled(enabled)
                        }
                    },
                    onAdvanceChanged = onSessionAdvanceChanged,
                )
            }
        }
    }
}

@Composable
private fun PermissionBanner(
    onEnableClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    OutlinedCard(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                imageVector = Icons.Default.Notifications,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(Modifier.width(12.dp))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.feature_settings_notification_disabled_title),
                    style = MaterialTheme.typography.titleSmall,
                )
                Text(
                    text = stringResource(Res.string.feature_settings_notification_disabled_description),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            TextButton(onClick = onEnableClick) {
                Text(stringResource(Res.string.feature_settings_notification_permission_enable))
            }
        }
    }
}

@Composable
private fun PaymentRemindersCard(
    enabled: Boolean,
    dayOfWeek: Int,
    hour: Int,
    minute: Int,
    isPermissionGranted: Boolean,
    onToggle: (Boolean) -> Unit,
    onDayChanged: (Int) -> Unit,
    onTimeChanged: (Int, Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    OutlinedCard(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text(
                text = stringResource(Res.string.feature_settings_notification_payment_title),
                style = MaterialTheme.typography.titleMedium,
            )

            Text(
                text = stringResource(Res.string.feature_settings_notification_payment_description),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = stringResource(Res.string.feature_settings_notification_payment_toggle),
                    modifier = Modifier.weight(1f),
                )
                Switch(checked = enabled, onCheckedChange = onToggle)
            }

            if (enabled && isPermissionGranted) {
                Text(
                    text = stringResource(Res.string.feature_settings_notification_payment_day_label),
                    style = MaterialTheme.typography.labelLarge,
                )
                Column {
                    DAY_LABELS.forEach { (value, labelRes) ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .selectable(
                                    selected = dayOfWeek == value,
                                    onClick = { onDayChanged(value) },
                                )
                                .padding(vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            RadioButton(
                                selected = dayOfWeek == value,
                                onClick = null,
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(stringResource(labelRes))
                        }
                    }
                }

                Text(
                    text = stringResource(Res.string.feature_settings_notification_payment_time_label),
                    style = MaterialTheme.typography.labelLarge,
                )
                TimeSelector(
                    hour = hour,
                    minute = minute,
                    onTimeChanged = onTimeChanged,
                )
            }
        }
    }
}

@Composable
private fun SessionRemindersCard(
    enabled: Boolean,
    advanceMinutes: Int,
    isPermissionGranted: Boolean,
    onToggle: (Boolean) -> Unit,
    onAdvanceChanged: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    OutlinedCard(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text(
                text = stringResource(Res.string.feature_settings_notification_session_title),
                style = MaterialTheme.typography.titleMedium,
            )

            Text(
                text = stringResource(Res.string.feature_settings_notification_session_description),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = stringResource(Res.string.feature_settings_notification_session_toggle),
                    modifier = Modifier.weight(1f),
                )
                Switch(checked = enabled, onCheckedChange = onToggle)
            }

            if (enabled && isPermissionGranted) {
                Text(
                    text = stringResource(Res.string.feature_settings_notification_session_advance_label),
                    style = MaterialTheme.typography.labelLarge,
                )
                Column {
                    ADVANCE_OPTIONS.forEach { (value, labelRes) ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .selectable(
                                    selected = advanceMinutes == value,
                                    onClick = { onAdvanceChanged(value) },
                                )
                                .padding(vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            RadioButton(
                                selected = advanceMinutes == value,
                                onClick = null,
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(stringResource(labelRes))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TimeSelector(
    hour: Int,
    minute: Int,
    onTimeChanged: (Int, Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    val timeSlots = listOf(
        7 to 0, 8 to 0, 9 to 0, 10 to 0, 11 to 0,
        12 to 0, 14 to 0, 16 to 0, 18 to 0,
    )

    Column(modifier = modifier) {
        timeSlots.forEach { (h, m) ->
            val label = "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .selectable(
                        selected = hour == h && minute == m,
                        onClick = { onTimeChanged(h, m) },
                    )
                    .padding(vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                RadioButton(
                    selected = hour == h && minute == m,
                    onClick = null,
                )
                Spacer(Modifier.width(8.dp))
                Text(label)
            }
        }
    }
}
